import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  BarChart3, 
  PieChart, 
  Calculator,
  FileText,
  Download,
  RefreshCw
} from 'lucide-react';
import { batchService } from '../services/batchService';
import { activityService } from '../services/activityService';
import { farmProfileService } from '../services/farmProfileService';

// Currency Configuration
const DEFAULT_CURRENCY = 'UGX';

const formatCurrency = (amount: number, currency: string = DEFAULT_CURRENCY): string => {
  return `${currency} ${amount.toLocaleString()}`;
};

const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  background: #f8fafc;
  color: #2d3748;
  min-height: 100vh;
`;


const ActionBar = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 30px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 25px;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  background: #2e7d32;
  color: white;

  &:hover {
    background: #1b5e20;
    transform: translateY(-1px);
  }

  &.secondary {
    background: #f5f5f5;
    color: #666;
    border: 1px solid #e0e0e0;
    
    &:hover {
      background: #e0e0e0;
    }
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 30px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e0e0e0;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e0e0e0;
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color};
`;

const StatTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #2e7d32;
  margin-bottom: 8px;
`;

const StatSubtext = styled.div`
  font-size: 0.9rem;
  color: #666;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e0e0e0;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const TableHeaderCell = styled.th`
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
`;

const TableCell = styled.td`
  padding: 12px;
  font-size: 0.9rem;
  color: #333;
`;


const FinancialAnalytics: React.FC = () => {
  const [batches, setBatches] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const loadedBatches = batchService.loadBatches();
      const loadedActivities = activityService.loadActivities();
      setBatches(loadedBatches);
      setActivities(loadedActivities);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Financial Analysis Functions
  const calculateRevenue = () => {
    const enabledRevenueSources = farmProfileService.getEnabledRevenueSources();
    
    let totalRevenue = 0;
    const revenueBreakdown: any[] = [];

    // Calculate revenue from batches
    const totalBirds = batches.reduce((sum, batch) => sum + (batch.initialBirds || 0), 0);
    const totalEggs = batches.reduce((sum, batch) => sum + (batch.eggsCollected || 0), 0);
    const activeBatches = batches.filter(batch => batch.status === 'active').length;

    enabledRevenueSources.forEach((source: any) => {
      if ('type' in source) {
        switch (source.type) {
          case 'chicken_sales':
            if (source.name.toLowerCase().includes('chicken')) {
              const revenue = totalBirds * source.estimatedValue;
              totalRevenue += revenue;
              revenueBreakdown.push({
                category: 'Chicken Sales',
                amount: revenue,
                quantity: totalBirds,
                unit: 'birds',
                pricePerUnit: source.estimatedValue
              });
            } else if (source.name.toLowerCase().includes('egg')) {
              const revenue = totalEggs * source.estimatedValue;
              totalRevenue += revenue;
              revenueBreakdown.push({
                category: 'Egg Sales',
                amount: revenue,
                quantity: totalEggs,
                unit: 'eggs',
                pricePerUnit: source.estimatedValue
              });
            }
            break;
          case 'manure_sales':
            const manureRevenue = activeBatches * source.estimatedValue;
            totalRevenue += manureRevenue;
            revenueBreakdown.push({
              category: 'Manure Sales',
              amount: manureRevenue,
              quantity: activeBatches,
              unit: 'batches',
              pricePerUnit: source.estimatedValue
            });
            break;
          case 'training':
            const trainingCount = activities.filter(a => 
              a.type === 'training' || 
              a.title.toLowerCase().includes('training') || 
              a.title.toLowerCase().includes('workshop')
            ).length;
            const trainingRevenue = trainingCount * source.estimatedValue;
            totalRevenue += trainingRevenue;
            revenueBreakdown.push({
              category: 'Training Services',
              amount: trainingRevenue,
              quantity: trainingCount,
              unit: 'sessions',
              pricePerUnit: source.estimatedValue
            });
            break;
          case 'farm_visits':
            const visitCount = activities.filter(a => 
              a.title.toLowerCase().includes('visit') || 
              a.title.toLowerCase().includes('tour')
            ).length;
            const visitRevenue = visitCount * source.estimatedValue;
            totalRevenue += visitRevenue;
            revenueBreakdown.push({
              category: 'Farm Visits',
              amount: visitRevenue,
              quantity: visitCount,
              unit: 'visits',
              pricePerUnit: source.estimatedValue
            });
            break;
          case 'consultancy':
            const consultCount = activities.filter(a => 
              a.title.toLowerCase().includes('consult') || 
              a.title.toLowerCase().includes('advisory')
            ).length;
            const consultRevenue = consultCount * source.estimatedValue;
            totalRevenue += consultRevenue;
            revenueBreakdown.push({
              category: 'Consultancy',
              amount: consultRevenue,
              quantity: consultCount,
              unit: 'sessions',
              pricePerUnit: source.estimatedValue
            });
            break;
        }
      }
    });

    return { totalRevenue, revenueBreakdown };
  };

  const calculateCostOfGoodsSold = () => {
    const totalBirds = batches.reduce((sum, batch) => sum + (batch.initialBirds || 0), 0);
    const totalFeedConsumption = batches.reduce((sum, batch) => sum + (batch.feedConsumption || 0), 0);
    
    // COGS Components
    const feedCost = totalFeedConsumption * 50; // UGX per kg
    const chickCost = totalBirds * 3000; // UGX per chick
    const medicalCost = batches.reduce((sum, batch) => sum + (batch.mortality || 0) * 2000, 0);
    
    const totalCOGS = feedCost + chickCost + medicalCost;
    
    return {
      totalCOGS,
      breakdown: [
        { category: 'Feed Costs', amount: feedCost, percentage: (feedCost / totalCOGS) * 100 },
        { category: 'Chick Costs', amount: chickCost, percentage: (chickCost / totalCOGS) * 100 },
        { category: 'Medical Costs', amount: medicalCost, percentage: (medicalCost / totalCOGS) * 100 }
      ]
    };
  };

  const calculateOperatingExpenses = () => {
    const batchCount = batches.length;
    
    // Operating Expenses
    const laborCost = batchCount * 50000; // UGX per batch
    const utilitiesCost = batchCount * 25000; // UGX per batch
    const maintenanceCost = batchCount * 15000; // UGX per batch
    const insuranceCost = batchCount * 10000; // UGX per batch
    const marketingCost = batchCount * 5000; // UGX per batch
    
    const totalOperatingExpenses = laborCost + utilitiesCost + maintenanceCost + insuranceCost + marketingCost;
    
    return {
      totalOperatingExpenses,
      breakdown: [
        { category: 'Labor', amount: laborCost, percentage: (laborCost / totalOperatingExpenses) * 100 },
        { category: 'Utilities', amount: utilitiesCost, percentage: (utilitiesCost / totalOperatingExpenses) * 100 },
        { category: 'Maintenance', amount: maintenanceCost, percentage: (maintenanceCost / totalOperatingExpenses) * 100 },
        { category: 'Insurance', amount: insuranceCost, percentage: (insuranceCost / totalOperatingExpenses) * 100 },
        { category: 'Marketing', amount: marketingCost, percentage: (marketingCost / totalOperatingExpenses) * 100 }
      ]
    };
  };

  const calculateFinancialMetrics = () => {
    const revenue = calculateRevenue();
    const cogs = calculateCostOfGoodsSold();
    const operatingExpenses = calculateOperatingExpenses();
    
    const grossProfit = revenue.totalRevenue - cogs.totalCOGS;
    const operatingProfit = grossProfit - operatingExpenses.totalOperatingExpenses;
    const netProfit = operatingProfit; // Assuming no other income/expenses
    
    const grossProfitMargin = revenue.totalRevenue > 0 ? (grossProfit / revenue.totalRevenue) * 100 : 0;
    const operatingProfitMargin = revenue.totalRevenue > 0 ? (operatingProfit / revenue.totalRevenue) * 100 : 0;
    const netProfitMargin = revenue.totalRevenue > 0 ? (netProfit / revenue.totalRevenue) * 100 : 0;
    
    return {
      revenue: revenue.totalRevenue,
      cogs: cogs.totalCOGS,
      grossProfit,
      operatingExpenses: operatingExpenses.totalOperatingExpenses,
      operatingProfit,
      netProfit,
      grossProfitMargin,
      operatingProfitMargin,
      netProfitMargin
    };
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
          <p>Loading financial data...</p>
        </div>
      </Container>
    );
  }

  const revenue = calculateRevenue();
  const cogs = calculateCostOfGoodsSold();
  const operatingExpenses = calculateOperatingExpenses();
  const metrics = calculateFinancialMetrics();

  return (
    <>
      <Container>

        <ActionBar>
          <Button onClick={loadData}>
            <RefreshCw size={20} />
            Refresh Data
          </Button>
          <Button className="secondary">
            <Download size={20} />
            Export P&L
          </Button>
        </ActionBar>

        {/* Key Financial Metrics */}
        <Section>
          <SectionTitle>
            <BarChart3 size={24} />
            Key Financial Metrics
          </SectionTitle>
          <Grid>
            <StatCard>
              <StatHeader>
                <StatIcon color="#2e7d32">
                  <DollarSign size={24} />
                </StatIcon>
                <StatTitle>Total Revenue</StatTitle>
              </StatHeader>
              <StatValue>{formatCurrency(metrics.revenue)}</StatValue>
              <StatSubtext>All revenue sources combined</StatSubtext>
            </StatCard>
            
            <StatCard>
              <StatHeader>
                <StatIcon color="#f44336">
                  <TrendingDown size={24} />
                </StatIcon>
                <StatTitle>Cost of Goods Sold</StatTitle>
              </StatHeader>
              <StatValue>{formatCurrency(metrics.cogs)}</StatValue>
              <StatSubtext>Direct production costs</StatSubtext>
            </StatCard>
            
            <StatCard>
              <StatHeader>
                <StatIcon color="#ff9800">
                    <Calculator size={24} />
                </StatIcon>
                <StatTitle>Gross Profit</StatTitle>
              </StatHeader>
              <StatValue>{formatCurrency(metrics.grossProfit)}</StatValue>
              <StatSubtext>{metrics.grossProfitMargin.toFixed(1)}% margin</StatSubtext>
            </StatCard>
            
            <StatCard>
              <StatHeader>
                <StatIcon color="#2196f3">
                  <PieChart size={24} />
                </StatIcon>
                <StatTitle>Operating Expenses</StatTitle>
              </StatHeader>
              <StatValue>{formatCurrency(metrics.operatingExpenses)}</StatValue>
              <StatSubtext>Indirect operating costs</StatSubtext>
            </StatCard>
            
            <StatCard>
              <StatHeader>
                <StatIcon color="#9c27b0">
                  <TrendingUp size={24} />
                </StatIcon>
                <StatTitle>Operating Profit</StatTitle>
              </StatHeader>
              <StatValue>{formatCurrency(metrics.operatingProfit)}</StatValue>
              <StatSubtext>{metrics.operatingProfitMargin.toFixed(1)}% margin</StatSubtext>
            </StatCard>
            
            <StatCard>
              <StatHeader>
                <StatIcon color={metrics.netProfit >= 0 ? "#2e7d32" : "#f44336"}>
                  <DollarSign size={24} />
                </StatIcon>
                <StatTitle>Net Profit</StatTitle>
              </StatHeader>
              <StatValue>{formatCurrency(metrics.netProfit)}</StatValue>
              <StatSubtext>{metrics.netProfitMargin.toFixed(1)}% margin</StatSubtext>
            </StatCard>
          </Grid>
        </Section>

        {/* Profit & Loss Statement */}
        <Section>
          <SectionTitle>
            <FileText size={24} />
            Profit & Loss Statement
          </SectionTitle>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Category</TableHeaderCell>
                <TableHeaderCell>Amount</TableHeaderCell>
                <TableHeaderCell>% of Revenue</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <tbody>
              <TableRow>
                <TableCell><strong>REVENUE</strong></TableCell>
                <TableCell><strong>{formatCurrency(metrics.revenue)}</strong></TableCell>
                <TableCell><strong>100.0%</strong></TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ paddingLeft: '20px' }}>Chicken Sales</TableCell>
                <TableCell>{formatCurrency(revenue.revenueBreakdown.find(r => r.category === 'Chicken Sales')?.amount || 0)}</TableCell>
                <TableCell>{((revenue.revenueBreakdown.find(r => r.category === 'Chicken Sales')?.amount || 0) / metrics.revenue * 100).toFixed(1)}%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ paddingLeft: '20px' }}>Egg Sales</TableCell>
                <TableCell>{formatCurrency(revenue.revenueBreakdown.find(r => r.category === 'Egg Sales')?.amount || 0)}</TableCell>
                <TableCell>{((revenue.revenueBreakdown.find(r => r.category === 'Egg Sales')?.amount || 0) / metrics.revenue * 100).toFixed(1)}%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ paddingLeft: '20px' }}>Other Revenue</TableCell>
                <TableCell>{formatCurrency(revenue.revenueBreakdown.filter(r => !['Chicken Sales', 'Egg Sales'].includes(r.category)).reduce((sum, r) => sum + r.amount, 0))}</TableCell>
                <TableCell>{(revenue.revenueBreakdown.filter(r => !['Chicken Sales', 'Egg Sales'].includes(r.category)).reduce((sum, r) => sum + r.amount, 0) / metrics.revenue * 100).toFixed(1)}%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>COST OF GOODS SOLD</strong></TableCell>
                <TableCell><strong>{formatCurrency(metrics.cogs)}</strong></TableCell>
                <TableCell><strong>{(metrics.cogs / metrics.revenue * 100).toFixed(1)}%</strong></TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ paddingLeft: '20px' }}>Feed Costs</TableCell>
                <TableCell>{formatCurrency(cogs.breakdown.find(c => c.category === 'Feed Costs')?.amount || 0)}</TableCell>
                <TableCell>{((cogs.breakdown.find(c => c.category === 'Feed Costs')?.amount || 0) / metrics.revenue * 100).toFixed(1)}%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ paddingLeft: '20px' }}>Chick Costs</TableCell>
                <TableCell>{formatCurrency(cogs.breakdown.find(c => c.category === 'Chick Costs')?.amount || 0)}</TableCell>
                <TableCell>{((cogs.breakdown.find(c => c.category === 'Chick Costs')?.amount || 0) / metrics.revenue * 100).toFixed(1)}%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ paddingLeft: '20px' }}>Medical Costs</TableCell>
                <TableCell>{formatCurrency(cogs.breakdown.find(c => c.category === 'Medical Costs')?.amount || 0)}</TableCell>
                <TableCell>{((cogs.breakdown.find(c => c.category === 'Medical Costs')?.amount || 0) / metrics.revenue * 100).toFixed(1)}%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>GROSS PROFIT</strong></TableCell>
                <TableCell><strong>{formatCurrency(metrics.grossProfit)}</strong></TableCell>
                <TableCell><strong>{metrics.grossProfitMargin.toFixed(1)}%</strong></TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>OPERATING EXPENSES</strong></TableCell>
                <TableCell><strong>{formatCurrency(metrics.operatingExpenses)}</strong></TableCell>
                <TableCell><strong>{(metrics.operatingExpenses / metrics.revenue * 100).toFixed(1)}%</strong></TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ paddingLeft: '20px' }}>Labor</TableCell>
                <TableCell>{formatCurrency(operatingExpenses.breakdown.find(o => o.category === 'Labor')?.amount || 0)}</TableCell>
                <TableCell>{((operatingExpenses.breakdown.find(o => o.category === 'Labor')?.amount || 0) / metrics.revenue * 100).toFixed(1)}%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ paddingLeft: '20px' }}>Utilities</TableCell>
                <TableCell>{formatCurrency(operatingExpenses.breakdown.find(o => o.category === 'Utilities')?.amount || 0)}</TableCell>
                <TableCell>{((operatingExpenses.breakdown.find(o => o.category === 'Utilities')?.amount || 0) / metrics.revenue * 100).toFixed(1)}%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ paddingLeft: '20px' }}>Maintenance</TableCell>
                <TableCell>{formatCurrency(operatingExpenses.breakdown.find(o => o.category === 'Maintenance')?.amount || 0)}</TableCell>
                <TableCell>{((operatingExpenses.breakdown.find(o => o.category === 'Maintenance')?.amount || 0) / metrics.revenue * 100).toFixed(1)}%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ paddingLeft: '20px' }}>Insurance</TableCell>
                <TableCell>{formatCurrency(operatingExpenses.breakdown.find(o => o.category === 'Insurance')?.amount || 0)}</TableCell>
                <TableCell>{((operatingExpenses.breakdown.find(o => o.category === 'Insurance')?.amount || 0) / metrics.revenue * 100).toFixed(1)}%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ paddingLeft: '20px' }}>Marketing</TableCell>
                <TableCell>{formatCurrency(operatingExpenses.breakdown.find(o => o.category === 'Marketing')?.amount || 0)}</TableCell>
                <TableCell>{((operatingExpenses.breakdown.find(o => o.category === 'Marketing')?.amount || 0) / metrics.revenue * 100).toFixed(1)}%</TableCell>
              </TableRow>
              <TableRow style={{ borderTop: '2px solid #333', backgroundColor: '#f8f9fa' }}>
                <TableCell><strong>NET PROFIT</strong></TableCell>
                <TableCell><strong>{formatCurrency(metrics.netProfit)}</strong></TableCell>
                <TableCell><strong>{metrics.netProfitMargin.toFixed(1)}%</strong></TableCell>
              </TableRow>
            </tbody>
          </Table>
        </Section>

        {/* Revenue Breakdown */}
        <Section>
          <SectionTitle>
            <PieChart size={24} />
            Revenue Breakdown
          </SectionTitle>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Revenue Source</TableHeaderCell>
                <TableHeaderCell>Amount</TableHeaderCell>
                <TableHeaderCell>Quantity</TableHeaderCell>
                <TableHeaderCell>Price per Unit</TableHeaderCell>
                <TableHeaderCell>% of Total</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <tbody>
              {revenue.revenueBreakdown.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.category}</TableCell>
                  <TableCell>{formatCurrency(item.amount)}</TableCell>
                  <TableCell>{item.quantity.toLocaleString()} {item.unit}</TableCell>
                  <TableCell>{formatCurrency(item.pricePerUnit)}</TableCell>
                  <TableCell>{(item.amount / metrics.revenue * 100).toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </Section>
      </Container>
    </>
  );
};

export default FinancialAnalytics;
